import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, Button, Chip, makeStyles, Menu, MenuItem } from '@material-ui/core';
import i18n from 'i18n-js';

import { ReservationDTO } from 'api/controllers/reservations';
import { PopupConfirmation } from 'components/shared';
import { theme } from 'config/theme';
import {
    formatGuestName,
    formatGuestsCount,
    formatGuestsLabel,
    formatReservationDate,
    getReservationFilterKey,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import {
    ReservationsTabFilter,
    useAppliedReservationFilter,
    useReservationMaps,
    useThunkDispatch,
} from '../hooks';
import ReservationDetailsPanel from './ReservationDetailsPanel';
import ReservationsFilterBar from './ReservationsFilterBar';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';

const useStyles = makeStyles({
    declineOption: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        width: 260,
        padding: '10px 24px',
    },
    declinePill: {
        minWidth: 100,
        textAlign: 'center',
        fontSize: 14,
        lineHeight: '18px',
        borderRadius: 100,
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
        backgroundColor: theme.palette.common.white,
    },
    declineHelper: {
        fontSize: 12,
        color: theme.palette.text.disabled,
    },
});

interface RequestsTabProps {
    filter: ReservationsTabFilter;
    onFilterChange: (filter: ReservationsTabFilter) => void;
}

export default function RequestsTab({ filter, onFilterChange }: RequestsTabProps) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const { requests, selectedRequestId, requestsView } = useAppSelector(
        (state) => state.reservations
    );
    const restaurantId = useAppSelector((state) => state.user.currentRestaurantId);
    const isRequestsLoaded = useAppSelector(
        (state) => state.reservations.requestsRestaurantId === state.user.currentRestaurantId
    );
    const { tables } = useReservationMaps();
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
    const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const { applied, isDebouncing } = useAppliedReservationFilter(filter);
    const appliedKey = getReservationFilterKey(applied);
    const isFiltered = !!requestsView.filter;
    const rows = isFiltered ? requestsView.items : requests;
    const isLoading = !isRequestsLoaded || requestsView.isLoading;
    const isShownFilterApplied =
        !isLoading &&
        (requestsView.filter ? getReservationFilterKey(requestsView.filter) : '|') === appliedKey;
    const selected = rows.find((request) => request.id === selectedRequestId);
    const now = Date.now();

    useEffect(() => {
        dispatch(reservationsStore.actionCreators.loadRequestsView(applied));
    }, [appliedKey, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isShownFilterApplied && selectedRequestId && !selected) {
            dispatch(reservationsStore.actionCreators.selectRequest(null));
        }
    }, [isShownFilterApplied, selectedRequestId, !!selected]); // eslint-disable-line react-hooks/exhaustive-deps

    const isOverdue = (request: ReservationDTO) =>
        Date.parse(request.reservationStartDateTime) < now;

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (request) =>
                formatReservationDate(request.reservationStartDateTime, 'dd.MM · HH:mm'),
            getSortValue: (request) => request.reservationStartDateTime,
            render: (request) => (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <span>
                        {formatReservationDate(request.reservationStartDateTime, 'dd.MM · HH:mm')}
                    </span>
                    {isOverdue(request) && (
                        <Chip
                            size="small"
                            label={i18n.t('reservations.overdue')}
                            style={{
                                backgroundColor: theme.palette.error.main,
                                color: theme.palette.common.white,
                            }}
                        />
                    )}
                </Box>
            ),
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (request) => request.guestsNumber,
        },
        {
            id: 'contact',
            label: i18n.t('reservations.columns.contact'),
            getText: (request) => request.phone || request.email || '',
        },
        {
            id: 'source',
            label: i18n.t('reservations.columns.source'),
            getText: (request) => i18n.t(`reservations.channels.${request.channel}`),
        },
        {
            id: 'received',
            label: i18n.t('reservations.columns.received'),
            getText: (request) => formatReservationDate(request.createDate, 'dd.MM HH:mm'),
            getSortValue: (request) => request.createDate,
        },
    ];

    const handleDecline = async (id: string) => {
        if (isDeclining) {
            return;
        }
        setIsDeclining(true);
        try {
            const result = await dispatch(reservationsStore.actionCreators.declineRequest(id));
            dispatch(
                result === 'declined'
                    ? globalStore.actionCreators.showToaster(
                          'success',
                          i18n.t('reservations.requestDeclined')
                      )
                    : globalStore.actionCreators.showToaster(
                          'error',
                          i18n.t('reservations.alreadyHandled')
                      )
            );
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            setIsDeclining(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0} pl={3}>
            <ReservationsFilterBar
                value={filter}
                onChange={onFilterChange}
                isLoading={isDebouncing || isLoading}
            />
            <Box display="flex" flex={1} minHeight={0}>
                <ReservationsTable
                    columns={columns}
                    reservations={rows}
                    sortLast={isOverdue}
                    selectedId={selectedRequestId}
                    onSelect={(id) => dispatch(reservationsStore.actionCreators.selectRequest(id))}
                    emptyText={
                        isFiltered
                            ? i18n.t('reservations.filters.noResults')
                            : i18n.t('reservations.noOpenRequests')
                    }
                    isLoading={isLoading}
                    pageResetKey={appliedKey}
                />
                {selected && (
                    <ReservationDetailsPanel
                        reservation={selected}
                        tables={tables}
                        onClose={() =>
                            dispatch(reservationsStore.actionCreators.selectRequest(null))
                        }
                        footer={
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={isDeclining}
                                    onClick={(event) => setMoreAnchor(event.currentTarget)}
                                >
                                    {i18n.t('reservations.more')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() =>
                                        history.push(
                                            `/reservations/requests/${selected.id}/process`
                                        )
                                    }
                                >
                                    {i18n.t('reservations.processRequest')}
                                </Button>
                            </>
                        }
                    />
                )}
            </Box>
            <Menu
                anchorEl={moreAnchor}
                open={!!moreAnchor}
                onClose={() => setMoreAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem
                    className={classes.declineOption}
                    onClick={() => {
                        setMoreAnchor(null);
                        setIsDeclineDialogOpen(true);
                    }}
                >
                    <span className={classes.declinePill}>{i18n.t('reservations.decline')}</span>
                    <span className={classes.declineHelper}>
                        {i18n.t('reservations.declineHelper')}
                    </span>
                </MenuItem>
            </Menu>
            {selected && (
                <PopupConfirmation
                    open={isDeclineDialogOpen}
                    isDismissible
                    close={() => setIsDeclineDialogOpen(false)}
                    title={i18n.t('reservations.declineTitle')}
                    description={i18n.t('reservations.declineDescription', {
                        name: formatGuestName(selected),
                        dateTime: formatReservationDate(
                            selected.reservationStartDateTime,
                            'EEE dd.MM.yyyy, HH:mm'
                        ),
                        guests: formatGuestsLabel(formatGuestsCount(selected)),
                    })}
                    activeBtn={i18n.t('reservations.decline')}
                    action={() => handleDecline(selected.id)}
                />
            )}
        </Box>
    );
}
